import { Box, Typography } from '@mui/material';
import { useCallback, useRef, useState } from 'react';
import frame_left from '../../../../../assets/images/dashboard/frame_left.webp';
import frame_right from '../../../../../assets/images/dashboard/frame_right.webp';
import close_icon from '../../../../../assets/svg/close_review.svg';
import checkmarkIcon from '../../../../../assets/svg/email_sent_checkmark.svg';
import Star from '../../../../../assets/svg/star.svg';
import StarPath from '../../../../../assets/svg/star_path.svg';
import starVideo from '../../../../../assets/videos/3s_3.mp4';
import { useAppState } from '../../../../../providers/AppProvider.hooks';
import { firebaseConfig } from '../../../../../services/firebase';
import { sendReview } from '../../../../../services/requests';
import BaseModal from '../../../../common/BaseModal';
import BaseButton from '../../Button';

export const ReviewModal = () => {
  const [isShown, setShown] = useState(
    window.localStorage.getItem('reviewModalShown') === 'true',
  );
  const [stars, setStars] = useState<number | null>(null);
  const [feedback, setFeedback] = useState<string>('');
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [loading, setLoading] = useState(false);
  const [sharedState, setSharedState] = useState<
    'liked' | 'not_liked' | 'feedback_sended' | null
  >(null);
  const { firebaseUserCredential } = useAppState();

  const closeReviewModal = () => {
    window.localStorage.setItem('reviewModalShown', 'true');
    setShown(true);
  };

  const handleClick = useCallback(() => {
    if (stars === null) return;
    if (stars > 3) {
      setSharedState('liked');
    } else {
      setSharedState('not_liked');
      setTimeout(() => {
        inputRef.current?.focus();
      }, 200);
    }
  }, [stars]);

  const handleLeaveReviewClick = () => {
    window.open(process.env.REACT_APP_REVIEW_URL, '_blank');
    closeReviewModal();
  };

  const handleSendFeedbackClick = async () => {
    console.log('firebaseUserCredential', firebaseUserCredential);

    if (!feedback || stars === null || !firebaseUserCredential) return;

    setLoading(true);

    try {
      await sendReview({
        user_id: firebaseUserCredential?.uid || '',
        project_id: firebaseConfig.projectId,
        name: firebaseUserCredential?.displayName ?? '',
        review: feedback,
        rating: stars,
      });

      setSharedState('feedback_sended');
    } catch (error) {
      console.error('Failed to send review:', error);
    } finally {
      setLoading(false);
    }
  };

  if (isShown) return null;

  return (
    <BaseModal isModalOpen={true} setModalOpen={() => {}} showClose={false}>
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          overflow: 'hidden',
          '.title': {
            fontFamily: 'Avenir Next',
            fontWeight: 600,
            fontSize: '28px',
            userSelect: 'none',
            lineHeight: 'normal',
            textAlign: 'center',
            color: '#040B1D',
            marginTop: '8px',
            marginBottom: '8px',
          },
          '.subTitle': {
            fontFamily: 'Avenir Next',
            fontWeight: 300,
            fontSize: '16px',
            userSelect: 'none',
            textAlign: 'center',
            lineHeight: 'normal',
            color: 'rgba(4, 11, 29, 0.60)',
            marginBottom: '16px',
          },
          '.review-button': {
            background: '#256BFD',
            width: '100%',
            height: '56px',
            padding: '13px 0px',
            borderRadius: '12px',
            fontFamily: 'Avenir Next',
            fontWeight: 600,
            fontSize: '16px',
            lineHeight: 'normal',
            color: '#FFF',
            textTransform: 'initial',
            transition: 'all 0.4s ease-in-out',
            '&:disabled': {
              color: '#FFF',
              opacity: 0.3,
            },
            '&:hover': {
              opacity: 0.8,
            },
          },
        }}
      >
        <img
          onClick={closeReviewModal}
          src={close_icon}
          alt="close"
          style={{
            position: 'absolute',
            top: '24px',
            right: '24px',
            cursor: 'pointer',
          }}
        />
        {sharedState === null ? (
          <>
            <video
              src={starVideo}
              autoPlay
              loop
              muted
              playsInline
              style={{
                width: '151px',
                height: '152px',
                aspectRatio: '151 / 152',
              }}
            />
            <Typography className="title">Worth 5 stars?</Typography>
            <Typography className="subTitle">
              Join thousands who already feel good about their choice here
            </Typography>
            <Box
              sx={{
                display: 'flex',
                gap: '8px',
                marginBottom: '24px',
              }}
            >
              {[1, 2, 3, 4, 5].map(el => (
                <Box
                  key={el}
                  onClick={() => {
                    setStars(el);
                  }}
                  sx={{
                    cursor: 'pointer',
                    img: {
                      width: '45px',
                      height: '42px',
                      minWidth: '45px',
                      minHeight: '42px',
                    },
                  }}
                >
                  {el - 1 < (stars ?? 0) ? (
                    <img src={Star} />
                  ) : (
                    <img src={StarPath} />
                  )}
                </Box>
              ))}
            </Box>
            <BaseButton
              disabled={stars === null}
              onClick={handleClick}
              className="review-button"
            >
              Share your rating
            </BaseButton>
          </>
        ) : (
          <>
            {sharedState === 'liked' && (
              <>
                <Typography className="title">Glad you liked it!</Typography>
                <Box
                  sx={{
                    padding: '20px 0',
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    img: {
                      width: '60px',
                      height: '122px',
                    },
                  }}
                >
                  <img
                    src={frame_left}
                    style={{
                      position: 'absolute',
                      left: 0,
                    }}
                  />
                  <Box
                    sx={{
                      borderRadius: '20px',
                      width: '100%',
                      background: 'rgba(255, 178, 29, 0.15)',
                      margin: '0 20px',
                      padding: '16px 48px 12px',
                      position: 'relative',
                      zIndex: 1,
                    }}
                  >
                    <Typography
                      sx={{
                        color: '#FFB21D',
                        textAlign: 'center',
                        fontFamily: 'Avenir Next',
                        fontSize: '16px',
                        fontWeight: 900,
                        lineHeight: 'normal',
                      }}
                    >
                      THANK YOU GIFT
                    </Typography>
                    <Typography
                      sx={{
                        color: '#232631',
                        textAlign: 'center',
                        fontFamily: 'Avenir Next',
                        fontSize: '28px',
                        fontWeight: 900,
                        lineHeight: 'normal',
                      }}
                    >
                      1 BONUS DAY
                    </Typography>
                  </Box>
                  <img
                    src={frame_right}
                    style={{
                      position: 'absolute',
                      right: 0,
                    }}
                  />
                </Box>
                <Typography
                  className="subTitle"
                  sx={{
                    color: 'rgba(4, 11, 29, 0.60)!important',
                  }}
                >
                  Share your experience with MyTraits and unlock 1 extra day of
                  free access
                </Typography>
                <BaseButton
                  onClick={handleLeaveReviewClick}
                  className="review-button"
                >
                  Leave a review
                </BaseButton>
              </>
            )}

            {sharedState === 'not_liked' && (
              <>
                <Typography className="title">Thanks for sharing</Typography>
                <Box
                  sx={{
                    width: '100%',
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    margin: '24px 0',
                    '& textarea': {
                      backgroundColor: '#fff',
                      width: '100%',
                      height: '100px',
                      borderRadius: '12px',
                      padding: '15px',
                      color: '#040B1D',
                      fontFamily: 'Avenir Next',
                      fontWeight: 400,
                      fontSize: '16px',
                      border: '1px solid #256BFD',
                      caretColor: '#6F33E4',

                      '&:focus-visible': {
                        outline: 'none',
                        border: '1px solid #256BFD',
                      },
                    },
                    '& textarea::placeholder': {
                      color: 'rgba(4, 11, 29, 0.60)',
                      fontFamily: 'Avenir Next',
                      fontWeight: 400,
                      fontSize: '16px',
                    },
                  }}
                >
                  <textarea
                    ref={inputRef}
                    autoFocus
                    rows={5}
                    placeholder="Write your review"
                    value={feedback}
                    onChange={e => {
                      setFeedback(e.target.value);
                    }}
                  ></textarea>
                </Box>
                <BaseButton
                  disabled={feedback === '' || loading}
                  onClick={handleSendFeedbackClick}
                  className="review-button"
                >
                  {loading ? 'Sending...' : 'Send feedback'}
                </BaseButton>
              </>
            )}

            {sharedState === 'feedback_sended' && (
              <>
                <img src={checkmarkIcon} width={112} height={112} />
                <Typography
                  className="title"
                  sx={{
                    fontSize: '20px!important',
                  }}
                >
                  Got it, thank you
                </Typography>
                <Typography className="subTitle">
                  We're building MetricsCheck with you — your feedback makes it
                  better
                </Typography>
                <BaseButton
                  onClick={closeReviewModal}
                  className="review-button"
                >
                  Continue
                </BaseButton>
              </>
            )}
          </>
        )}
      </Box>
    </BaseModal>
  );
};
